import * as React from "react";
import { useValidateShippingData } from "../../../../../../../utility-hooks/modules/PaymentMethodsList/Payments/Adyen/GooglePay/utility-hooks/use-validate-shipping-data.js";
import { useTransactionInfo } from "./use-transaction-info.js";
import { useAdyen } from "../../../../../../../../../../../../context/Adyen/context/Adyen/context.js";
const getSharedConfig = ({ locale, cartPrices, currency, country, transactionInfo, onAuthorized }, config, merchantId, gatewayMerchantId) => ({
  buttonLocale: locale,
  buttonSizeMode: "fill",
  allowedAuthMethods: config?.googlepay?.allowedAuthMethods,
  allowedCardNetworks: config?.googlepay?.allowedCardNetworks,
  amount: {
    value: cartPrices?.totalPrice ?? 0,
    currency
  },
  countryCode: country,
  transactionInfo,
  onAuthorized: (paymentData) => {
    onAuthorized?.(paymentData);
  },
  environment: config?.googlepay?.environment ?? "TEST",
  configuration: {
    gatewayMerchantId: gatewayMerchantId ?? "",
    merchantId,
    merchantName: config?.googlepay?.merchantName
  }
});
const getRegularConfig = (configProps, config, merchantId, gatewayMerchantId) => {
  const sharedConfig = getSharedConfig(configProps, config, merchantId, gatewayMerchantId);
  return {
    ...sharedConfig,
    buttonType: "pay",
    type: "paywithgoogle",
    ...configProps.onClick ? { onClick: configProps.onClick } : {},
    isExpress: false
  };
};
const getExpressConfig = (configProps, config, merchantId, gatewayMerchantId) => {
  const sharedConfig = getSharedConfig(configProps, config, merchantId, gatewayMerchantId);
  const expressConfigWithoutShippingAddress = {
    ...sharedConfig,
    buttonType: "checkout",
    type: "googlepay",
    isExpress: false,
    emailRequired: true,
    billingAddressRequired: true,
    billingAddressParameters: {
      format: "FULL",
      phoneNumberRequired: true
    },
    shippingAddressParameters: {
      allowedCountryCodes: [configProps.country],
      phoneNumberRequired: true
    },
    shippingOptionRequired: false
  };
  return configProps.shippingAddressRequired ? {
    ...expressConfigWithoutShippingAddress,
    callbackIntents: ["SHIPPING_ADDRESS", "PAYMENT_AUTHORIZATION"],
    shippingAddressRequired: true,
    paymentDataCallbacks: {
      onPaymentDataChanged(intermediatePaymentData) {
        return new Promise((resolve) => {
          const { callbackTrigger, shippingAddress } = intermediatePaymentData;
          const paymentDataRequestUpdate = {};
          if (callbackTrigger === "SHIPPING_ADDRESS" && configProps.validateShippingData) {
            const { isPaymentDataValid, error } = configProps.validateShippingData(shippingAddress);
            if (!isPaymentDataValid) {
              paymentDataRequestUpdate.error = error;
            }
          }
          resolve(paymentDataRequestUpdate);
        });
      },
      onPaymentAuthorized(intermediatePaymentData) {
        return new Promise((resolve) => {
          const { shippingAddress } = intermediatePaymentData;
          const paymentDataRequestUpdate = {};
          if (configProps.validateShippingData) {
            const { isPaymentDataValid, error } = configProps.validateShippingData(shippingAddress);
            if (!isPaymentDataValid) {
              paymentDataRequestUpdate.error = error;
              paymentDataRequestUpdate.transactionState = "ERROR";
            }
          }
          paymentDataRequestUpdate.transactionState = "SUCCESS";
          resolve(paymentDataRequestUpdate);
        });
      }
    }
  } : expressConfigWithoutShippingAddress;
};
const useGooglePayConfig = (props) => {
  const { config } = useAdyen();
  const [mapTransactionInfo] = useTransactionInfo();
  const validateShippingData = useValidateShippingData();
  const propsRef = React.useRef(props);
  React.useEffect(() => {
    propsRef.current = props;
  }, [props]);
  const regularGooglePayConfig = (merchantId, gatewayMerchantId) => {
    const { cartPrices, lineItems, currency, country } = propsRef.current;
    const transactionInfo = mapTransactionInfo({ cartPrices, lineItems, currency, country });
    return getRegularConfig(
      {
        ...propsRef.current,
        transactionInfo
      },
      config,
      merchantId,
      gatewayMerchantId
    );
  };
  const expressGooglePayConfig = (merchantId, gatewayMerchantId, shippingAddressRequired = false) => {
    const { cartPrices, lineItems, currency, country } = propsRef.current;
    const transactionInfo = mapTransactionInfo({ cartPrices, lineItems, currency, country });
    return getExpressConfig(
      {
        ...propsRef.current,
        cartPrices: propsRef.current.cartPrices,
        transactionInfo,
        validateShippingData,
        shippingAddressRequired
      },
      config,
      merchantId,
      gatewayMerchantId
    );
  };
  return { regularGooglePayConfig, expressGooglePayConfig };
};
export {
  useGooglePayConfig
};
//# sourceMappingURL=use-google-pay-config.js.map
